import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Settings, FileText, CheckCircle, XCircle, Package, Undo2, Truck, AlertTriangle, HeartPulse } from 'lucide-react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 30_000;

// Mapeo de íconos por tipo (deben coincidir con NOTIFICATION_TYPES del backend)
const ICON_MAP = {
  FileText, CheckCircle, XCircle, Package, Undo2, Truck, AlertTriangle, HeartPulse, Bell,
};

// Cache simple del audio para evitar re-fetch
let _chimeAudio = null;
function playChime() {
  try {
    if (!_chimeAudio) {
      // Tono breve generado vía WebAudio (sin assets externos)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.30);
      o.start();
      o.stop(ctx.currentTime + 0.32);
    }
  } catch (_e) {
    // Audio bloqueado por el navegador hasta que el usuario interactúe — silenciamos.
  }
}

function timeAgo(isoDate) {
  if (!isoDate) return '';
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(isoDate);
  const date = new Date(hasTz ? isoDate : isoDate + 'Z');
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'ahora';
  if (diffMin < 60) return `hace ${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `hace ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `hace ${diffD}d`;
  return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const previousUnreadRef = useRef(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      const newCount = res.data.unread_count || 0;
      // Si subió respecto del valor anterior → reproducir chime
      if (newCount > previousUnreadRef.current && previousUnreadRef.current >= 0) {
        playChime();
      }
      previousUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch (_e) {
      // Silenciar errores de polling para no llenar la consola
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data || []);
    } catch (_e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling cada 30s
  useEffect(() => {
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchUnreadCount]);

  // Cargar lista al abrir dropdown
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await axios.put(`${API}/notifications/${notif.notification_id}/read`, {}, { withCredentials: true });
      } catch (_e) { /* ignore */ }
      setNotifications((prev) => prev.map((n) => n.notification_id === notif.notification_id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
      previousUnreadRef.current = Math.max(0, previousUnreadRef.current - 1);
    }
    if (notif.link) {
      setIsOpen(false);
      navigate(notif.link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      previousUnreadRef.current = 0;
    } catch (_e) { /* ignore */ }
  };

  const COLOR_CLASSES = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-950',
    green: 'text-green-600 bg-green-50 dark:bg-green-950',
    red: 'text-red-600 bg-red-50 dark:bg-red-950',
    purple: 'text-purple-600 bg-purple-50 dark:bg-purple-950',
    orange: 'text-orange-600 bg-orange-50 dark:bg-orange-950',
    teal: 'text-teal-600 bg-teal-50 dark:bg-teal-950',
    amber: 'text-amber-600 bg-amber-50 dark:bg-amber-950',
    pink: 'text-pink-600 bg-pink-50 dark:bg-pink-950',
    gray: 'text-gray-600 bg-gray-50 dark:bg-gray-950',
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell-trigger"
          aria-label="Notificaciones"
        >
          <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-blue-600' : ''}`} />
          {unreadCount > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-96 p-0"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-semibold text-sm">Notificaciones</div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                className="h-7 px-2 text-xs"
                data-testid="mark-all-read-btn"
                title="Marcar todas como leídas"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Marcar todas
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => { setIsOpen(false); navigate('/profile?tab=notifications'); }}
              data-testid="notification-settings-btn"
              title="Preferencias de notificaciones"
            >
              <Settings className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <ScrollArea className="h-[420px]">
          {loading && notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="notification-empty-state">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No tenés notificaciones todavía
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {notifications.map((n) => {
                const Icon = ICON_MAP[n.icon] || Bell;
                const colorCls = COLOR_CLASSES[n.color] || COLOR_CLASSES.gray;
                return (
                  <li
                    key={n.notification_id}
                    data-testid={`notification-item-${n.notification_id}`}
                    onClick={() => handleNotificationClick(n)}
                    className={`px-4 py-3 cursor-pointer hover:bg-accent transition-colors ${!n.is_read ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${colorCls}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-tight ${!n.is_read ? 'font-semibold' : 'font-medium'}`}>
                            {n.title}
                          </p>
                          {!n.is_read && (
                            <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                          {!n.is_read && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleNotificationClick({ ...n, link: null }); }}
                              className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                              data-testid={`mark-read-${n.notification_id}`}
                            >
                              <Check className="w-3 h-3" /> marcar leída
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
